import { SupabaseClient } from '@supabase/supabase-js'
import { fetchAllPaged } from './pagination'

// ─── Types ────────────────────────────────────────────────────────────────────

export type BucketKey = 'current' | 'd31_60' | 'd61_90' | 'd91_120' | 'd120plus'

export const BUCKET_DEFS: Array<{ key: BucketKey; label: string }> = [
  { key: 'current', label: '0–30 days' },
  { key: 'd31_60', label: '31–60 days' },
  { key: 'd61_90', label: '61–90 days' },
  { key: 'd91_120', label: '91–120 days' },
  { key: 'd120plus', label: '120+ days' },
]

export type LedgerLine = {
  contactId: string
  date: string // journal entry_date (YYYY-MM-DD)
  debit: number
  credit: number
  journalId: string
  sourceType: string | null
}

// A single-document charge for the per-location document basis (payments already
// netted into `open`).
export type DocCharge = {
  contactId: string
  date: string
  open: number
  ref?: string | null
}

export type AgedCharge = {
  journalId: string | null
  date: string
  original: number
  open: number
  bucket: BucketKey
  sourceType: string | null
}

export type AgedRow = {
  contactId: string
  contactName: string
  phone: string | null
  total: number
  current: number
  d31_60: number
  d61_90: number
  d91_120: number
  d120plus: number
  charges: AgedCharge[]
}

export type BucketSummary = {
  key: BucketKey
  label: string
  count: number
  total: number
}

export type AgedReport = {
  rows: AgedRow[]
  buckets: BucketSummary[]
  grandTotal: number
  contactCount: number
}

export type ContactMeta = { name: string; phone: string | null }

export function emptyReport(): AgedReport {
  return {
    rows: [],
    buckets: BUCKET_DEFS.map(d => ({ key: d.key, label: d.label, count: 0, total: 0 })),
    grandTotal: 0,
    contactCount: 0,
  }
}

export function bucketOf(days: number): BucketKey {
  if (days <= 30) return 'current'
  if (days <= 60) return 'd31_60'
  if (days <= 90) return 'd61_90'
  if (days <= 120) return 'd91_120'
  return 'd120plus'
}

function daysBetween(asAt: Date, date: string): number {
  return Math.floor((asAt.getTime() - new Date(date).getTime()) / 86400000)
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (supabase: SupabaseClient, t: string): any => (supabase as any).from(t)

// Paginated fetch of AR/AP journal lines via the embedded non-voided inner join.
// No journal-id IN list (that URL is rejected/truncated for large tenants); and
// paged with .range() so we never hit PostgREST's ~1000-row response cap — the
// raw lines are the FIFO input, so completeness is mandatory for reconciliation.
export async function fetchLedgerLines(
  supabase: SupabaseClient,
  tenantId: string,
  accountIds: string[],
): Promise<LedgerLine[]> {
  if (!accountIds.length) return []
  const PAGE = 1000
  const out: LedgerLine[] = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await db(supabase, 'journal_entry_lines')
      .select(
        'contact_id, debit_amount, credit_amount, journal_id, ' +
          'journal_entries!inner(entry_date, is_voided, source_type)',
      )
      .eq('tenant_id', tenantId)
      .in('account_id', accountIds)
      .not('contact_id', 'is', null)
      .not('journal_entries.is_voided', 'is', true)
      .order('journal_id', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1)
    if (error) throw error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows = (data ?? []) as any[]
    for (const r of rows) {
      const je = Array.isArray(r.journal_entries) ? r.journal_entries[0] : r.journal_entries
      if (!je || !r.contact_id) continue
      out.push({
        contactId: r.contact_id,
        date: je.entry_date,
        debit: r.debit_amount ?? 0,
        credit: r.credit_amount ?? 0,
        journalId: r.journal_id,
        sourceType: je.source_type ?? null,
      })
    }
    if (rows.length < PAGE) break
  }
  return out
}

export type AccountLine = {
  accountId: string
  contactId: string | null
  date: string
  debit: number
  credit: number
  sourceType: string | null
}

// fetchLedgerLines' sibling WITHOUT the contact_id requirement — for account-level
// aggregation (COA balances, cash/bank, tax liabilities) where lines legitimately
// have no contact. Paginated + NULL-safe void exclusion, ordered by the unique PK
// for stable paging. Omit accountIds to read every account for the tenant.
export async function fetchAccountLines(
  supabase: SupabaseClient,
  tenantId: string,
  accountIds?: string[],
): Promise<AccountLine[]> {
  if (accountIds && !accountIds.length) return []
  const rows = await fetchAllPaged<{
    id: string
    account_id: string
    contact_id: string | null
    debit_amount: number | null
    credit_amount: number | null
    journal_entries:
      | { entry_date: string; source_type: string | null }
      | Array<{ entry_date: string; source_type: string | null }>
      | null
  }>((from, to) => {
    let q = db(supabase, 'journal_entry_lines')
      .select('id, account_id, contact_id, debit_amount, credit_amount, journal_entries!inner(entry_date, is_voided, source_type)')
      .eq('tenant_id', tenantId)
      .not('journal_entries.is_voided', 'is', true)
    if (accountIds && accountIds.length) q = q.in('account_id', accountIds)
    return q.order('id', { ascending: true }).range(from, to)
  })
  return rows.map(r => {
    const je = Array.isArray(r.journal_entries) ? r.journal_entries[0] : r.journal_entries
    return {
      accountId: r.account_id,
      contactId: r.contact_id ?? null,
      date: je?.entry_date ?? '',
      debit: r.debit_amount ?? 0,
      credit: r.credit_amount ?? 0,
      sourceType: je?.source_type ?? null,
    }
  })
}

// ─── Aging engines ────────────────────────────────────────────────────────────

function buildRow(
  contactId: string,
  charges: AgedCharge[], // each already carries its post-FIFO `open`
  asAt: Date,
  meta: ContactMeta | undefined,
): AgedRow | null {
  const b: Record<BucketKey, number> = {
    current: 0, d31_60: 0, d61_90: 0, d91_120: 0, d120plus: 0,
  }
  const open: AgedCharge[] = []
  let total = 0
  for (const c of charges) {
    if (c.open <= 0.005) continue
    const key = bucketOf(daysBetween(asAt, c.date))
    c.bucket = key
    b[key] += c.open
    total += c.open
    open.push(c)
  }
  if (total <= 0.005) return null
  return {
    contactId,
    contactName: meta?.name ?? 'Unknown',
    phone: meta?.phone ?? null,
    total,
    ...b,
    charges: open,
  }
}

function finalize(rows: AgedRow[]): AgedReport {
  rows.sort((a, b) => b.total - a.total)
  const buckets: BucketSummary[] = BUCKET_DEFS.map(d => ({ key: d.key, label: d.label, count: 0, total: 0 }))
  let grandTotal = 0
  for (const r of rows) {
    grandTotal += r.total
    for (const b of buckets) {
      const v = r[b.key]
      if (v > 0.005) { b.count += 1; b.total += v }
    }
  }
  return { rows, buckets, grandTotal, contactCount: rows.length }
}

// FIFO ledger aging (all-locations). `side` flips the charge sign: AR charges are
// debit-positive, AP charges are credit-positive. Payments (the opposite sign)
// are applied oldest-charge-first; payment DATES are not used for allocation —
// only charge dates drive aging (standard aged-trial-balance convention). Sum of
// a contact's buckets therefore equals its ledger balance by construction.
export function ageContacts(
  lines: LedgerLine[],
  asAt: Date,
  side: 'AR' | 'AP',
  meta: Map<string, ContactMeta>,
): AgedReport {
  const byContact = new Map<string, LedgerLine[]>()
  for (const l of lines) {
    const arr = byContact.get(l.contactId)
    if (arr) arr.push(l)
    else byContact.set(l.contactId, [l])
  }

  const rows: AgedRow[] = []
  for (const [contactId, cLines] of byContact) {
    const charges: AgedCharge[] = []
    let paymentTotal = 0
    for (const l of cLines) {
      const net = side === 'AR' ? l.debit - l.credit : l.credit - l.debit
      if (net > 0.005) {
        charges.push({ journalId: l.journalId, date: l.date, original: net, open: net, bucket: 'current', sourceType: l.sourceType })
      } else if (net < -0.005) {
        paymentTotal += -net // credit note / receipt / refund reduces oldest debt
      }
    }
    // Oldest first — opening-balance charges sort in by their own entry_date.
    charges.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    let remaining = paymentTotal
    for (const c of charges) {
      const applied = Math.min(remaining, c.original)
      c.open = c.original - applied
      remaining -= applied
    }
    // remaining > 0 ⇒ payments exceeded charges ⇒ net advance ⇒ buildRow returns
    // null (total 0), excluding the contact from the debtor/creditor list.
    const row = buildRow(contactId, charges, asAt, meta.get(contactId))
    if (row) rows.push(row)
  }
  return finalize(rows)
}

// Document-basis aging (per-location). One charge per document, already net of
// payments/returns; bucket by document date. No FIFO (documents carry their own
// outstanding). Reconciles to documents at the location, not to the ledger total.
export function ageDocuments(
  docs: DocCharge[],
  asAt: Date,
  meta: Map<string, ContactMeta>,
): AgedReport {
  const byContact = new Map<string, AgedCharge[]>()
  for (const d of docs) {
    if (d.open <= 0.005) continue
    const charge: AgedCharge = { journalId: null, date: d.date, original: d.open, open: d.open, bucket: 'current', sourceType: d.ref ?? null }
    const arr = byContact.get(d.contactId)
    if (arr) arr.push(charge)
    else byContact.set(d.contactId, [charge])
  }
  const rows: AgedRow[] = []
  for (const [contactId, charges] of byContact) {
    const row = buildRow(contactId, charges, asAt, meta.get(contactId))
    if (row) rows.push(row)
  }
  return finalize(rows)
}
